import { Close } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, MenuItem, Popover, PopoverOrigin, SxProps, TextField, Theme, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GridSingleSelectOption } from '..';
import { Entity } from 'ui-component/icons';
import { EntityPopover } from 'ui-component/records/RecordGrid/components/EntityPopover';

export type ListProps = {
    open: boolean;
    anchorEl: HTMLElement | null;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
    containerRef: React.RefObject<HTMLDivElement | null>;
    searchText: string;
    options: GridSingleSelectOption[];
    onClose: () => void;
    onChangeSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSelectOption: (selectedId: number | string) => () => void;
    onClearSearch: () => void;
    fitContent?: boolean;
    PopoverSx?: SxProps<Theme> | undefined;
    PopoverPropsAnchorOrigin?: PopoverOrigin | undefined;
    PopoverPropsTransformOrigin?: PopoverOrigin | undefined;
    isEntity?: boolean;
};

export const List = ({
    open,
    anchorEl,
    searchInputRef,
    containerRef,
    searchText,
    options,
    onClose,
    onChangeSearch,
    onSelectOption,
    onClearSearch,
    fitContent,
    PopoverSx,
    PopoverPropsAnchorOrigin,
    PopoverPropsTransformOrigin,
    isEntity
}: ListProps) => {
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
    const [currentEntityId, setCurrentEntityId] = useState<number | string | null>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, entityId: number | string) => {
        setPopoverAnchorEl(event.currentTarget);
        setCurrentEntityId(entityId);
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            setPopoverAnchorEl(null);
            setCurrentEntityId(null);
        }, 100);
    };

    const openPopover = Boolean(popoverAnchorEl);

    return (
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: fitContent ? 'left' : 'right', ...PopoverPropsAnchorOrigin }}
            transformOrigin={{ vertical: 'top', horizontal: fitContent ? 'left' : 'right', ...PopoverPropsTransformOrigin }}
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    mt: '5px',
                    width: fitContent ? 'fit-content' : containerRef.current?.clientWidth || '200px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }
            }}
            sx={{
                transform: fitContent ? `translateX(-${containerRef.current?.clientWidth}px)` : undefined,
                ...PopoverSx
            }}
        >
            <TextField
                variant="filled"
                size="small"
                inputRef={searchInputRef}
                InputProps={{
                    hiddenLabel: true,
                    disableUnderline: true,
                    sx: { bgcolor: '#F5F6F7' },
                    endAdornment: searchText.length ? (
                        <InputAdornment position="end">
                            <IconButton aria-label="clear search" onClick={onClearSearch} edge="end">
                                <Close />
                            </IconButton>
                        </InputAdornment>
                    ) : undefined
                }}
                value={searchText}
                onChange={onChangeSearch}
                autoFocus
                fullWidth
            />
            {options.length ? (
                options.map((el) => (
                    <MenuItem
                        sx={{ border: 'solid 1px #D9D9D9', height: '40px', px: '12px', bgcolor: '#F5F6F7' }}
                        key={el.key || el.id}
                        onClick={onSelectOption(el.id)}
                    >
                        {el.renderName || (
                            <Typography
                                fontSize="14px"
                                sx={{
                                    overflow: 'hidden',
                                    width: isEntity ? '90%' : '100%',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {el.name}
                            </Typography>
                        )}
                        {isEntity && (
                            <Box
                                data-entity-key={`entity-${el.name}-${el.id}`}
                                onMouseEnter={(event) => handleMouseEnter(event, el.id)}
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Entity sx={{ width: '1rem', height: '1rem', cursor: 'pointer' }} />
                                <EntityPopover
                                    isDropdown
                                    entityId={el.id}
                                    open={openPopover && currentEntityId === el.id}
                                    anchorEl={popoverAnchorEl}
                                    onClose={handleMouseLeave}
                                />
                            </Box>
                        )}
                    </MenuItem>
                ))
            ) : (
                <Typography fontSize="14px" sx={{ py: 2, px: 1 }} color="#C6C4C4" align="center">
                    <FormattedMessage id="noOptionsAvailable" />
                </Typography>
            )}
        </Popover>
    );
};
